import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Ticket {
  id: string
  ticket_number: string
  title: string
  category: string
  priority: string
  status: string
  requester: string
  department: string
  assigned_to: string | null
  created_date: string
  sla_due: string
}

interface Asset {
  id: string
  asset_tag: string
  asset_type: string
  manufacturer: string
  model: string
  status: string
  assigned_to: string | null
  purchase_cost: number
}

export default function ServiceDeskPage() {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [assets, setAssets] = useState<Asset[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [ticketRes, assetRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/servicedesk/tickets'),
      fetch(API_BASE + '/servicedesk/assets'),
      fetch(API_BASE + '/servicedesk/statistics')
    ])
    if (ticketRes.ok) setTickets((await ticketRes.json()).tickets || [])
    if (assetRes.ok) setAssets((await assetRes.json()).assets || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'resolved': case 'closed': case 'active': return 'bg-green-100 text-green-800'
      case 'open': case 'in_progress': return 'bg-blue-100 text-blue-800'
      case 'pending': case 'in_repair': return 'bg-yellow-100 text-yellow-800'
      case 'critical': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800'
      case 'high': return 'bg-orange-100 text-orange-800'
      case 'medium': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">IT Service Desk</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Ticket</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Tickets</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.open_tickets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Critical</div>
              <div className="text-2xl font-bold text-red-600">{statistics.critical_tickets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Resolution (hrs)</div>
              <div className="text-2xl font-bold">{statistics.avg_resolution_hrs}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Assets</div>
              <div className="text-2xl font-bold text-green-600">{statistics.total_assets}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">SLA Compliance</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.sla_compliance}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Support Tickets</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {tickets.slice(0, 12).map(ticket => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-sm">{ticket.ticket_number}</div>
                      <div className="text-xs text-gray-500 truncate max-w-xs">{ticket.title}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{ticket.requester}</div>
                      <div className="text-xs text-gray-500">{ticket.department}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getPriorityColor(ticket.priority)}`}>{ticket.priority}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(ticket.status)}`}>{ticket.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">IT Assets</h3>
            <div className="space-y-3">
              {assets.slice(0, 10).map(asset => (
                <div key={asset.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm">{asset.asset_tag}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(asset.status)}`}>{asset.status}</span>
                  </div>
                  <div className="text-xs text-gray-500 capitalize">{asset.asset_type} - {asset.manufacturer}</div>
                  <div className="text-xs text-gray-600 mt-1">{asset.assigned_to || 'Unassigned'}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
